import React, {useState} from "react";
import axios from "axios";
import {Menu, Transition} from "@headlessui/react";
import {Fragment} from "react";
import toast from "react-hot-toast";
import {format, parseISO, isValid} from "date-fns"; // For date validation/formatting

// Reusing getAuthHeaders from your previous components
const Baseurl = process.env.REACT_APP_BASEURL;

const getAuthHeaders = () => {
  const getToken = localStorage.getItem("eftd-admin-token");
  let token: string | null = null;
  try {
    if (getToken) {
      const tokenObject = JSON.parse(getToken);
      token = tokenObject?.data?.data?.token;
    }
  } catch (e) {
    console.error("Failed to parse token from localStorage:", e);
    toast.error("Authentication session error. Please log in again.");
    return null;
  }

  if (!token) {
    toast.error("No authentication token found. Please log in.");
    return null;
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

// Define types for better clarity (optional, but good practice)
interface ExportOrdersProps {
  userId: string; // The ID of the user whose orders are being exported
}

const ExportOrdersComponent: React.FC<ExportOrdersProps> = ({userId}) => {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const handleExport = async (closeMenu: () => void) => {
    if (!fromDate || !toDate) {
      toast.error("Please select both From Date and To Date.");
      return;
    }

    const start = parseISO(fromDate);
    const end = parseISO(toDate);

    if (!isValid(start) || !isValid(end)) {
      toast.error("Invalid date format. Please use the date pickers.");
      return;
    }

    if (start > end) {
      toast.error("From Date cannot be after To Date.");
      return;
    }

    const toastId = toast.loading("Preparing order data for export...");
    setIsExporting(true);

    try {
      const config = getAuthHeaders();
      if (!config || !Baseurl || !userId) {
        toast.dismiss(toastId);
        toast.error(
          "Export failed: Missing required data (token, base URL, or user ID)."
        );
        setIsExporting(false);
        return;
      }

      // Construct query parameters
      const params = {
        userId: userId,
        since: format(start, "yyyy-MM-dd'T'00:00:00'Z'"),
        before: format(end, "yyyy-MM-dd'T'23:59:59'Z'"),
        ...(fileName && {exportName: fileName}),
      };

      const queryString = new URLSearchParams(params).toString();
      const exportEndpoint = `${Baseurl}/api/admin/orders/export-data?${queryString}`;

      const response = await axios.get(exportEndpoint, {
        ...config,
        responseType: "blob", // Important for file downloads
      });

      if (response.status === 200) {
        // Get the filename from the Content-Disposition header, or use custom/default
        const contentDisposition = response.headers["content-disposition"];
        let suggestedFileName = `orders_export_${format(
          new Date(),
          "yyyyMMdd_HHmm"
        )}.csv`; // Default CSV
        if (fileName) {
          suggestedFileName = `${fileName}.csv`; // Use custom name if provided
        } else if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch && filenameMatch[1]) {
            suggestedFileName = filenameMatch[1];
          }
        }

        // Create a blob URL and trigger download
        const blob = new Blob([response.data], {type: "text/csv"});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", suggestedFileName);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url); // Clean up the URL

        toast.success("Order data exported successfully!", {id: toastId});
        closeMenu();
      } else {
        toast.error(
          `Export failed: Server responded with status ${response.status}`,
          {id: toastId}
        );
      }
    } catch (error: any) {
      toast.dismiss(toastId);
      if (error.response) {
        // Handle error response
        if (error.response.data instanceof Blob) {
          // If the error response is a blob, try to read it as text
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorText = reader.result as string;
              const errorJson = JSON.parse(errorText);
              toast.error(
                `Export failed: ${
                  errorJson.message || "Unknown error occurred"
                }`
              );
            } catch (e) {
              toast.error("Export failed: Unable to parse error response");
            }
          };
          reader.readAsText(error.response.data);
        } else {
          toast.error(
            `Export failed: ${
              error.response.data?.message || "Unknown error occurred"
            }`
          );
        }
      } else {
        toast.error("Export failed: Network error or server unavailable");
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
          disabled={isExporting} // Disable button during export
        >
          {isExporting ? "Exporting..." : "Export Orders"}
          {/* Heroicon "ChevronDownIcon" can be added here if you have @heroicons/react */}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-300 ring-opacity-5 focus:outline-none p-4">
          <div className="py-1">
            <div className="mb-4">
              <label
                htmlFor="fromDate"
                className="block text-sm font-medium text-gray-700"
              >
                From Date:
              </label>
              <input
                type="date"
                id="fromDate"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="toDate"
                className="block text-sm font-medium text-gray-700"
              >
                To Date:
              </label>
              <input
                type="date"
                id="toDate"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="fileName"
                className="block text-sm font-medium text-gray-700"
              >
                Customize File Name (Optional):
              </label>
              <input
                type="text"
                id="fileName"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="e.g., Q2_Sales_Report"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
              />
            </div>
            <div className="text-right">
              <Menu.Item>
                {(
                  {close} // Get the close function from headless UI
                ) => (
                  <button
                    onClick={() => handleExport(close)}
                    disabled={isExporting}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting ? "Exporting..." : "Export"}
                  </button>
                )}
              </Menu.Item>
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default ExportOrdersComponent;
